'use client'

import Loader from '@/components/common/loader'
import {
  getChampionshipGridPositions,
  getChampionshipGridPositionsLabel,
} from '@/helpers/championship'
import apiFetch from '@/store/helpers/api-fetch'
import { notFound } from 'next/navigation'
import { useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import ChampionshipEventCoundown from './championship-event-countdown'
import ManageChampionshipEventDrivers from './manage-championship-event-drivers'
import ManageChampionshipEventHeader from './manage-championship-event-header'
import ManageChampionshipEventSessions from './manage-championship-event-sessions'
import ManageChampionshipEventStats from './manage-championship-event-stats'
import ManageChampionshipEventToolbar from './manage-championship-event-toolbar'

export default function ManageChampionshipEvent({
  communityId,
  championshipId,
  eventId,
}) {
  const {
    data: event,
    isLoading,
    error,
  } = useSWRImmutable(
    {
      url: `community/${communityId}/championship/${championshipId}/event/${eventId}`,
      searchParams: {
        management: true,
        includeUnscheduled: true,
        include: {
          track: true,
          sessions: {
            include: {
              splits: true,
            },
            orderBy: {
              targetDateTime: 'asc',
            },
          },
          championship: {
            include: {
              community: {
                select: {
                  id: true,
                  slug: true,
                },
              },
              carClasses: {
                select: {
                  gridPositions: true,
                },
              },
              splits: {
                select: {
                  id: true,
                  name: true,
                  color: true,
                },
              },
              attendees: {
                where: {
                  status: 'accepted',
                },
                include: {
                  team: true,
                  split: true,
                  carClass: true,
                },
              },
              _count: {
                select: {
                  events: {
                    where: {
                      deleteTs: null,
                    },
                  },
                },
              },
            },
          },
          _count: {
            select: {
              attendees: {
                where: {
                  status: 'registered',
                },
              },
            },
          },
        },
      },
    },
    apiFetch,
  )

  const grid = useMemo(() => {
    if (!event) return null
    return {
      gridPositions: getChampionshipGridPositions(event.championship),
      gridPositionsLabel: getChampionshipGridPositionsLabel(event.championship),
      isGridUnlimited: event.championship.isGridUnlimited === true,
    }
  }, [event])

  if (error) notFound()
  if (isLoading)
    return <Loader size="lg" className="absolute h-screen w-full" />

  return (
    <div className="col-span-full mb-5 mt-2 flex flex-col gap-5">
      <ManageChampionshipEventHeader event={event} />

      <div className="divide" />

      <ManageChampionshipEventToolbar event={event} />
      <div className="divide" />

      <ChampionshipEventCoundown event={event} />

      <ManageChampionshipEventStats event={event} {...grid} />

      <div className="divide" />

      <ManageChampionshipEventSessions event={event} />

      <div className="divide" />

      <ManageChampionshipEventDrivers
        event={event}
        championshipAttendees={event.championship.attendees}
        {...grid}
      />
    </div>
  )
}
